/**
 * FeaturedOrganization — thin wrapper that fetches the active
 * `featured_organization` promotions and renders the reusable
 * banner components from `components/Importable/FeaturedBanner`.
 *
 * The `bannerType` stored on the first promotion in the placement controls
 * which layout is used:
 *   "grid"   → GridFeaturedBanner  (multi-card, default)
 *   "single" → SingleFeaturedBanner (one item at a time, image-left layout)
 *
 * This file is responsible only for:
 *   1. Fetching promotions (including bannerType)
 *   2. Mapping `FeaturedOrgItem` → the appropriate item shape
 *   3. Picking the accent colour and `data-select-target`
 */

import React, { useMemo, useCallback } from "react";
import { useAppDispatch } from "@/src/store/hooks";
import { useGetPromotionsQuery, api } from "@/src/services/api";
import { orange } from "@/theme/colors";
import {
    GridFeaturedBanner,
    GridFeaturedBannerSkeleton,
    GridFeaturedItem,
    SingleFeaturedBanner,
    SingleFeaturedBannerSkeleton,
    SingleFeaturedItem,
    featuredOrgDescription,
} from "@/components/Importable/FeaturedBanner";
import { useDataVersionCheck } from "@/hooks/useDataVersionCheck";

export interface FeaturedOrgItem {
    id?: string;
    slug?: string;
    orgName: string;
    orgDescripTitle?: string;
    description?: string;
    logoUrl?: string;
    bannerImages?: string[];
    infoSection?: { location?: { city?: string } };
}

export interface FeaturedOrganizationSectionProps {
    /** Full list of available orgs (will be filtered to promoted ones). */
    orgs: FeaturedOrgItem[];
    /** Called when the user clicks "Visit". */
    onVisit: (org: FeaturedOrgItem) => void;
    /** Optional image URL transformer (e.g. assetUrl). */
    transformImageUrl?: (url: string) => string;
    /** Show a skeleton placeholder while orgs are loading. */
    loading?: boolean;
    /**
     * `data-select-target` attribute for the selector tool. Defaults to
     * "Featured Organization" for backwards compatibility with the existing
     * task-capture registry.
     */
    dataSelectTarget?: string;
}

export const FeaturedOrganizationSection: React.FC<
    FeaturedOrganizationSectionProps
> = ({
    orgs,
    onVisit,
    transformImageUrl,
    loading = false,
    dataSelectTarget = "Featured Organization",
}) => {
    const dispatch = useAppDispatch();
    const { data: featuredOrgPromos } =
        useGetPromotionsQuery("featured_organization");

    // When the server bumps the "promotions" data version (e.g. an admin
    // changes the banner type or the promoted list), invalidate the RTK Query
    // cache so this component refetches and re-renders with the latest data.
    useDataVersionCheck(
        "promotions",
        useCallback(() => {
            dispatch(api.util.invalidateTags(["Promotions"]));
        }, [dispatch]),
    );

    // The bannerType on all promotions in this placement is kept in sync by
    // the admin bulk-update endpoint. Read it from the first item; default to
    // "grid" while data is loading or for new placements.
    const storedBannerType = featuredOrgPromos?.[0]?.bannerType ?? "grid";

    // Build a lookup from targetId → promotion imageUrl for the single-banner mapping.
    const promoImageByTargetId = useMemo(() => {
        const map: Record<string, string | null> = {};
        for (const promo of featuredOrgPromos ?? []) {
            map[promo.targetId] = promo.imageUrl;
        }
        return map;
    }, [featuredOrgPromos]);

    // Build the list of promoted orgs in the order the promotions specify.
    const featuredList: FeaturedOrgItem[] = useMemo(() => {
        if (!featuredOrgPromos || featuredOrgPromos.length === 0) return [];
        return featuredOrgPromos
            .map((promo) =>
                orgs.find(
                    (o) => o.id === promo.targetId || o.slug === promo.targetId,
                ),
            )
            .filter(Boolean) as FeaturedOrgItem[];
    }, [orgs, featuredOrgPromos]);

    // Grid needs at least 3 promoted items to fill its slots — with fewer,
    // fall back to the single rotating banner automatically.
    const bannerType =
        storedBannerType === "grid" && featuredList.length < 3
            ? "single"
            : storedBannerType;

    // Items for GridFeaturedBanner.
    const gridItems: GridFeaturedItem[] = useMemo(
        () =>
            featuredList.map((org) => ({
                id: org.id,
                slug: org.slug,
                name: org.orgName,
                description: featuredOrgDescription(
                    org.orgDescripTitle,
                    org.description,
                ),
                logoUrl: org.logoUrl,
                city: org.infoSection?.location?.city,
            })),
        [featuredList],
    );

    // Items for SingleFeaturedBanner.
    // The promotion's own imageUrl is used as the hero image when available,
    // falling back to the org's bannerImages or logoUrl.
    const singleItems: SingleFeaturedItem[] = useMemo(
        () =>
            featuredList.map((org) => {
                const promoImage =
                    promoImageByTargetId[org.id ?? org.slug ?? ""];
                return {
                    id: org.id || org.slug || org.orgName,
                    image:
                        promoImage ||
                        org.bannerImages?.[0] ||
                        org.logoUrl ||
                        "",
                    images: org.bannerImages,
                    name: org.orgName,
                    description: featuredOrgDescription(
                        org.orgDescripTitle,
                        org.description,
                    ),
                    link: `/organizations/${org.id || org.slug}`,
                };
            }),
        [featuredList, promoImageByTargetId],
    );

    // Promotions are checked FIRST: while they're unknown (in flight or
    // errored) or resolved empty, render nothing at all — the skeleton only
    // holds when promotions exist and their targets are still resolving.
    const promosExist = !!featuredOrgPromos?.length;

    if (featuredList.length === 0) {
        if (promosExist && loading) {
            return bannerType === "single" ? (
                <SingleFeaturedBannerSkeleton accentColor={orange.primary} />
            ) : (
                <GridFeaturedBannerSkeleton
                    accentColor={orange.primary}
                    dataSelectTarget={dataSelectTarget}
                />
            );
        }
        return null;
    }

    if (bannerType === "single") {
        return (
            <SingleFeaturedBanner
                items={singleItems}
                onCta={(item) => {
                    const original =
                        featuredList.find(
                            (o) => o.id === item.id || o.slug === item.id,
                        ) ?? featuredList[0];
                    onVisit(original);
                }}
                sectionLabel="Featured Organization"
                ctaLabel="Visit"
                accentColor={orange.primary}
                accentDark={orange.dark}
                dataSelectTarget={dataSelectTarget}
            />
        );
    }

    return (
        <GridFeaturedBanner
            list={gridItems}
            onVisit={(item) => {
                const original =
                    featuredList.find(
                        (o) =>
                            (item.id && o.id === item.id) ||
                            (item.slug && o.slug === item.slug),
                    ) ?? featuredList[0];
                onVisit(original);
            }}
            transformImageUrl={transformImageUrl}
            sectionLabel="Featured Organization"
            ctaLabel={(name) => `Visit ${name}`}
            accentColor={orange.primary}
            accentDark={orange.dark}
            dataSelectTarget={dataSelectTarget}
        />
    );
};

export default FeaturedOrganizationSection;
